import type { CostEvidence, EstimateRequest, EvidenceKind, EvidenceObservation } from "../estimating/evidence";
import type { Cents } from "../domain/money";

/**
 * Estimate evidence ranking (Phase 9E, spec §26D) — pure and deterministic.
 *
 * Providers supply attributable numbers; this decides which one the app
 * RECOMMENDS and says why, in sentences a person can argue with. It never
 * fetches anything, never stores anything, and never invents a number: if
 * the only evidence is the template's assumption, the recommendation is the
 * template's assumption and it says so plainly.
 *
 * Ranking is a fixed order per cost basis, not a score:
 *
 *   material          manual → current offer → company history → network → template
 *   labor / other     manual → company history → network → template
 *
 * A current offer for the exact product beats an average of what similar
 * products cost last year; nobody's labor is bought off a shelf, so for
 * labor the company's own history leads. Evidence that fails the
 * sufficiency bar is not silently dropped — it is reported as rejected,
 * with the reason.
 */

/** n/d rounded half-up (non-negative). */
function roundDiv(n: number, d: number): number {
  return Math.floor((n + d / 2) / d);
}

/** Cents ×100 → whole cents, half-up. Sub-cent precision dies here, once. */
export function toCents(unitCostCentsX100: number): Cents {
  return roundDiv(unitCostCentsX100, 100);
}

/** quantity(×100) × unitCost(cents ×100) → cents, half-up. */
export function amountForQuantity(unitCostCentsX100: number, quantityX100: number): Cents {
  return roundDiv(unitCostCentsX100 * quantityX100, 10000);
}

export interface SufficiencyRules {
  /** Below this many observations, history is indicative, not a recommendation. */
  minObservations: number;
  /** Older than this and history is stale for a recommendation (days). */
  maxAgeDays: number;
}

export const DEFAULT_SUFFICIENCY: SufficiencyRules = {
  minObservations: 2,
  maxAgeDays: 730,
};

export interface EvidenceStatistics {
  medianX100: number;
  lowX100: number;
  highX100: number;
  count: number;
  /** ISO date of the most recent observation. */
  latest: string | null;
}

/** Median (even counts average the middle pair, half-up) plus the range. */
export function summarize(observations: EvidenceObservation[]): EvidenceStatistics | null {
  if (observations.length === 0) return null;
  const values = observations.map((o) => o.unitCostCentsX100).sort((a, b) => a - b);
  const mid = Math.floor(values.length / 2);
  const medianX100 =
    values.length % 2 === 1 ? values[mid] : roundDiv(values[mid - 1] + values[mid], 2);
  const dates = observations.map((o) => o.date).filter((d) => d).sort();
  return {
    medianX100,
    lowX100: values[0],
    highX100: values[values.length - 1],
    count: values.length,
    latest: dates.length > 0 ? dates[dates.length - 1] : null,
  };
}

export function daysBetween(from: string, to: string): number {
  const a = Date.parse(from);
  const b = Date.parse(to);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

const ORDER_MATERIAL: EvidenceKind[] = [
  "manual-override",
  "product-offer",
  "organization-history",
  "network-market",
  "template-assumption",
];
const ORDER_WORK: EvidenceKind[] = [
  "manual-override",
  "organization-history",
  "network-market",
  "product-offer",
  "template-assumption",
];

export interface RejectedEvidence {
  evidence: CostEvidence;
  /** Why it did not become the recommendation — always shown. */
  reason: string;
}

export interface EstimateRecommendation {
  /** The evidence the app recommends, or null when there is none at all. */
  chosen: CostEvidence | null;
  /** Everything else worth seeing, in ranked order. */
  alternatives: CostEvidence[];
  /** Evidence that could not be recommended, with the reason. */
  rejected: RejectedEvidence[];
  /** Why the chosen evidence won, and what it means. */
  rationale: string[];
  confidence: "high" | "medium" | "low";
  /** The recommended per-unit cost in whole cents (null with no evidence). */
  unitCostCents: Cents | null;
  /** The extended amount for the requested quantity, when one was given. */
  amountCents: Cents | null;
}

/**
 * Rank the gathered evidence for one estimate request.
 *
 * `request` supplies the cost basis, the unit and "now"; evidence in a
 * different unit is never compared (no silent conversions), matching the
 * assembly engine's rule.
 */
export function rankEvidence(
  evidence: CostEvidence[],
  request: EstimateRequest,
  rules: SufficiencyRules = DEFAULT_SUFFICIENCY,
): EstimateRecommendation {
  const order = request.subject.costBasis === "material" ? ORDER_MATERIAL : ORDER_WORK;
  const rejected: RejectedEvidence[] = [];
  const eligible: CostEvidence[] = [];

  for (const item of evidence) {
    if (item.unit !== request.subject.unit) {
      rejected.push({
        evidence: item,
        reason: `Priced per ${item.unit}, but this line is estimated per ${request.subject.unit}. Berrier Reno never converts units behind your back.`,
      });
      continue;
    }
    if (item.sampleSize === 0 || item.observations.length === 0) {
      if (item.kind !== "template-assumption" && item.kind !== "manual-override") {
        rejected.push({ evidence: item, reason: "Nothing behind this number." });
        continue;
      }
    }
    if (item.provisional) {
      rejected.push({
        evidence: item,
        reason: "The work behind these numbers is not finished, so the cost is not final. Shown as context only.",
      });
      continue;
    }
    if (item.kind === "organization-history") {
      if (item.sampleSize < rules.minObservations) {
        rejected.push({
          evidence: item,
          reason: `Only ${item.sampleSize} comparable job${item.sampleSize === 1 ? "" : "s"} on record — ${rules.minObservations} needed before history leads. Shown as context.`,
        });
        continue;
      }
      const age = item.asOf ? daysBetween(item.asOf, request.asOf) : Number.MAX_SAFE_INTEGER;
      if (age > rules.maxAgeDays) {
        rejected.push({
          evidence: item,
          reason: `The most recent comparable job is ${Math.round(age / 30)} months old — too old to lead. Shown as context.`,
        });
        continue;
      }
    }
    eligible.push(item);
  }

  const rank = (item: CostEvidence): number => {
    const index = order.indexOf(item.kind);
    return index === -1 ? order.length : index;
  };
  eligible.sort((a, b) => {
    const byKind = rank(a) - rank(b);
    if (byKind !== 0) return byKind;
    // Deterministic within a kind: more evidence first, then newer, then id.
    if (a.sampleSize !== b.sampleSize) return b.sampleSize - a.sampleSize;
    if ((a.asOf ?? "") !== (b.asOf ?? "")) return (b.asOf ?? "").localeCompare(a.asOf ?? "");
    return a.id.localeCompare(b.id);
  });

  const chosen = eligible[0] ?? null;
  const alternatives = eligible.slice(1);
  const rationale: string[] = [];
  let confidence: "high" | "medium" | "low" = "low";

  if (!chosen) {
    rationale.push(
      "No cost evidence yet for this line. The estimate stands on whatever you or the template set — and says so, rather than pretending to know.",
    );
  } else {
    switch (chosen.kind) {
      case "manual-override":
        rationale.push("Your own decision — it takes precedence over everything Berrier Reno works out.");
        confidence = "high";
        break;
      case "product-offer":
        // Only the product actually selected on the line may be described
        // as pricing THIS line; anything else is a catalog candidate.
        if (chosen.quality === "exact") {
          rationale.push("A current offer for the product selected on this line.");
          confidence = "high";
        } else {
          rationale.push(
            "A current offer for a product in this material catalog — NOT the product selected on this line (none is selected, or a different one is).",
          );
          confidence = "medium";
        }
        if (chosen.market && request.market.zip && chosen.market !== request.market.zip) {
          rationale.push(
            `Observed for ZIP ${chosen.market}, not ${request.market.zip} — store prices differ by market.`,
          );
          confidence = "medium";
        }
        break;
      case "organization-history":
        rationale.push(
          `What this company actually paid on ${chosen.sampleSize} comparable job${chosen.sampleSize === 1 ? "" : "s"}${chosen.market ? ` in ${chosen.market}` : ""} — the median, so one unusual job does not move it.`,
        );
        confidence = chosen.sampleSize >= rules.minObservations + 2 ? "high" : "medium";
        break;
      case "network-market":
        rationale.push("Regional market data.");
        confidence = "medium";
        break;
      case "template-assumption":
        rationale.push(
          "The assembly template's assumption — a starting point, not something this company has actually paid.",
        );
        confidence = "low";
        break;
    }
    if (chosen.lowCentsX100 !== null && chosen.highCentsX100 !== null && chosen.lowCentsX100 !== chosen.highCentsX100) {
      rationale.push(
        `Range behind it: ${formatUnit(chosen.lowCentsX100)} – ${formatUnit(chosen.highCentsX100)} per ${chosen.unit}.`,
      );
    }
    if (chosen.asOf) rationale.push(`Most recent evidence: ${chosen.asOf}.`);
    if (alternatives.length > 0) {
      rationale.push(
        `Also considered: ${alternatives.map((a) => `${a.providerLabel} at ${formatUnit(a.unitCostCentsX100)}`).join(", ")}.`,
      );
    }
    for (const r of rejected) {
      rationale.push(`${r.evidence.providerLabel}: ${r.reason}`);
    }
  }

  const unitCostCents = chosen ? toCents(chosen.unitCostCentsX100) : null;
  const amountCents =
    chosen && request.quantityX100 !== null
      ? amountForQuantity(chosen.unitCostCentsX100, request.quantityX100)
      : null;

  return { chosen, alternatives, rejected, rationale, confidence, unitCostCents, amountCents };
}

/** "$12.34" from cents ×100, for rationale sentences (display only). */
function formatUnit(centsX100: number): string {
  const cents = toCents(centsX100);
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100).toLocaleString("en-US")}.${String(abs % 100).padStart(2, "0")}`;
}

export interface EvidenceCoverage {
  total: number;
  byKind: Record<EvidenceKind | "none", number>;
  /** Lines whose price rests on nothing but a template assumption. */
  assumptionOnly: number;
}

/**
 * How much of an estimate stands on evidence — the honesty summary shown
 * beside a total, so "smart" never hides "guessed".
 */
export function summarizeCoverage(kinds: (EvidenceKind | null)[]): EvidenceCoverage {
  const byKind: Record<EvidenceKind | "none", number> = {
    "manual-override": 0,
    "organization-history": 0,
    "product-offer": 0,
    "template-assumption": 0,
    "network-market": 0,
    none: 0,
  };
  for (const kind of kinds) byKind[kind ?? "none"] += 1;
  return {
    total: kinds.length,
    byKind,
    assumptionOnly: byKind["template-assumption"] + byKind.none,
  };
}
